/**
 * Word counts and goals on the page (part 19b log, principles 2–5, tables D, G and B): the open chapter's words, counted
 * in the find worker once the writer pauses (never on the keystroke path); today's words, the change between counts of
 * one editor added to its book's day; the word count dialog's counts (the chapter, its selection, the book); and the
 * goals. The workspace, the worker, the clock and the timers are injected, so the logic runs in tests; the state is Solid
 * signals, so the toolbar and the dialog follow it.
 */
import { createSignal } from 'solid-js';
import type { Accessor, Setter } from 'solid-js';
import type { EditorHandle, FindTexts } from '../shell/workspace.ts';
import type { BookWriting } from '../shell/settings.ts';
import type { FindOutcome, FindRequest } from '../find/host.ts';
import { addToday, localDay, reached, readGoal, todayOf } from './tally.ts';

/** D2: how long after the writer's last change the open chapter is counted, in milliseconds (the autosave's pause). */
export const COUNT_DELAY_MS = 500;

/** What the controller needs of the workspace. */
export interface CountPlace {
  readonly bookId: () => string | null;
  readonly chapterId: () => string | null;
  readonly editor: () => EditorHandle | null;
  /** B1: the open book's live chapters, the open one as typed. */
  readonly texts: () => Promise<FindTexts | null>;
  /** G1: a book's goals and today's words. */
  readonly writing: (bookId: string) => BookWriting;
  /** G1, D2: changes a book's goals or today's words, and stores them (D8: in memory only when they can't be). */
  readonly changeWriting: (bookId: string, change: Partial<BookWriting>) => void;
}

/** What the controller needs; everything with an effect is injected. */
export interface CountControllerOptions {
  readonly place: CountPlace;
  /** Runs a job in the find worker (`FindHost.run`). */
  readonly run: (request: FindRequest) => Promise<FindOutcome>;
  /** Runs `run` after `ms`; returns what cancels it. */
  readonly schedule: (ms: number, run: () => void) => () => void;
  /** The clock, in milliseconds, and the device's zone at a time, in minutes ahead of UTC (D6). */
  readonly now: () => number;
  readonly zone: (at: number) => number;
}

/** Table B: what the dialog shows of the counts: under way, counted, or not (B4). */
export type Measure =
  | { readonly kind: 'idle' }
  | { readonly kind: 'counting' }
  | {
      readonly kind: 'counted';
      /** The open chapter's words, and its selection's (null with none open, or nothing selected). */
      readonly chapter: number | null;
      readonly selection: number | null;
      readonly book: number;
      /** B3: chapters left out because they couldn't be read. */
      readonly unreadable: number;
    }
  | { readonly kind: 'failed' };

/** G6: the daily goal reached: the words it asked for. */
export interface GoalNews {
  readonly goal: number;
}

/** One editor followed (D1): its book and chapter, the last text sent to be counted, and its last count answered. */
interface Followed {
  readonly editor: EditorHandle;
  readonly bookId: string;
  readonly chapterId: string;
  sent: string | null;
  counted: number | null;
}

/**
 * Part 11d, H5: the open book's words outside one chapter, from a whole-book count, so each count of that chapter gives
 * the book's words without reading the book again.
 */
interface Shelf {
  readonly bookId: string;
  readonly chapterId: string | null;
  readonly others: number;
}

/** A Solid signal as a pair. */
interface Signal<T> {
  readonly get: Accessor<T>;
  readonly set: Setter<T>;
}

function signal<T>(value: T): Signal<T> {
  const [get, set] = createSignal(value, { equals: false });
  return { get, set };
}

/** Tables D, G and B: the counts and the goals. */
export class CountController {
  readonly #options: CountControllerOptions;
  readonly #words = signal<number | null>(null);
  readonly #measure = signal<Measure>({ kind: 'idle' });
  readonly #news = signal<GoalNews | null>(null);
  #followed: Followed | null = null;
  /** The text of the editor followed when it was made (D1), sent as its reference with the first count. */
  #reference: string | null = null;
  #cancel: (() => void) | null = null;
  /** B5, B7: the dialog's last count asked for; an answer to any other is dropped. */
  #measures = 0;
  /** G6: each book and day whose goal was said reached. */
  readonly #said = new Set<string>();
  /** Part 11d, H5: the last whole-book count's other chapters, the count asked last, and what cancels the one waiting. */
  #shelf: Shelf | null = null;
  #shelfCounts = 0;
  #shelfCancel: (() => void) | null = null;

  constructor(options: CountControllerOptions) {
    this.#options = options;
  }

  /** The open chapter's words as last counted; null before the first count, or with none open. */
  readonly words = (): number | null => this.#words.get();
  /** Table B: the dialog's counts. */
  readonly measure = (): Measure => this.#measure.get();
  /** G6: the daily goal just reached, or null. */
  readonly news = (): GoalNews | null => this.#news.get();

  /** The device's local day now (D6). */
  #day(): string {
    const now = this.#options.now();
    return localDay(now, this.#options.zone(now));
  }

  /** D6: the open book's words typed today on this device. */
  today(): number {
    const bookId = this.#options.place.bookId();
    return bookId === null ? 0 : todayOf(this.#options.place.writing(bookId), this.#day());
  }

  /** G1: the open book's goals and today's words, or none with no book open. */
  goals(): BookWriting | null {
    const bookId = this.#options.place.bookId();
    return bookId === null ? null : this.#options.place.writing(bookId);
  }

  /**
   * D1, D3: the workspace's editor changed (opened, switched, reopened, closed): the one followed until now is counted
   * once more when its last text wasn't (D3), and the new one is followed from its text now (D1).
   */
  editorChanged(): void {
    const editor = this.#options.place.editor();
    const before = this.#followed;
    if (before?.editor === editor) return;
    this.#cancelPending();
    if (before !== null) this.#flush(before);
    const bookId = this.#options.place.bookId();
    const chapterId = this.#options.place.chapterId();
    this.#words.set(null);
    this.#followed = editor === null || bookId === null || chapterId === null ? null : { editor, bookId, chapterId, sent: null, counted: null };
    this.#reference = this.#followed === null ? null : this.#followed.editor.parsed.text;
    if (this.#followed !== null) this.#later(this.#followed);
  }

  /** D2: the open chapter's text changed; it is counted once the writer pauses. */
  edited(): void {
    if (this.#followed !== null) this.#later(this.#followed);
  }

  /** Counts `followed` {@link COUNT_DELAY_MS} after the last call. */
  #later(followed: Followed): void {
    this.#cancelPending();
    this.#cancel = this.#options.schedule(COUNT_DELAY_MS, () => {
      this.#cancel = null;
      this.#flush(followed);
    });
  }

  #cancelPending(): void {
    this.#cancel?.();
    this.#cancel = null;
  }

  /** D1–D3: sends `followed`'s reference first when it hasn't been sent, then its text now when it wasn't sent last. */
  #flush(followed: Followed): void {
    if (followed.sent === null && followed === this.#followed && this.#reference !== null) {
      this.#count(followed, this.#reference);
      this.#reference = null;
    }
    const text = followed.editor.parsed.text;
    if (text !== followed.sent) this.#count(followed, text);
  }

  /** D2, D7, D9: counts `text` of `followed`; the answer's change since its last answered count goes to today's words. */
  #count(followed: Followed, text: string): void {
    followed.sent = text;
    void this.#options.run({ kind: 'count', chapters: [{ id: followed.chapterId, text }], selection: null }).then((outcome) => {
      const words = outcome.kind === 'counted' ? outcome.counts[0] : undefined;
      // D7: a count stopped or failed adds nothing; the next one's change covers it.
      if (words === undefined) return;
      const before = followed.counted;
      followed.counted = words;
      if (followed === this.#followed) this.#words.set(words);
      const changed = before !== null && words !== before;
      const change = { ...(changed ? this.#add(followed.bookId, words - before) : {}), ...this.#shelve(followed.bookId, followed.chapterId, words, changed) };
      if (Object.keys(change).length > 0) this.#options.place.changeWriting(followed.bookId, change);
    });
  }

  /**
   * Part 11d, H5: the book's words from a count of its open chapter, for the home page, when they differ from those
   * stored; `changed` stamps the book as written in now. No words before a whole-book count of the same book and chapter.
   */
  #shelve(bookId: string, chapterId: string, words: number, changed: boolean): Partial<BookWriting> {
    const shelf = this.#shelf;
    const total = shelf !== null && shelf.bookId === bookId && shelf.chapterId === chapterId ? shelf.others + words : null;
    return {
      ...(total !== null && this.#options.place.writing(bookId).words !== total ? { words: total } : {}),
      ...(changed ? { edited: this.#options.now() } : {}),
    };
  }

  /**
   * Part 11d, H5: the open book, its open chapter or its chapters changed (or it was read again): the whole book is
   * counted once the writer pauses, in the find worker, and its words stored for the home page. A later call drops an
   * answer still to come.
   */
  shelfChanged(): void {
    this.#shelfCancel?.();
    const id = ++this.#shelfCounts;
    this.#shelf = null;
    this.#shelfCancel = this.#options.schedule(COUNT_DELAY_MS, () => {
      this.#shelfCancel = null;
      const { place } = this.#options;
      const bookId = place.bookId();
      const chapterId = place.chapterId();
      if (bookId === null) return;
      void place.texts().then(async (texts) => {
        if (id !== this.#shelfCounts || texts === null) return;
        const outcome = await this.#options.run({ kind: 'count', chapters: texts.chapters, selection: null });
        if (id !== this.#shelfCounts || outcome.kind !== 'counted') return;
        const open = texts.chapters.findIndex((chapter) => chapter.id === chapterId);
        let total = 0;
        let counted = 0;
        for (const [index, words] of outcome.counts.entries()) {
          total += words;
          if (index === open) counted = words;
        }
        this.#shelf = { bookId, chapterId: open < 0 ? null : chapterId, others: total - counted };
        // The open chapter's own count since the book's texts were read, when there is one, is the newer.
        const followed = this.#followed;
        const words = followed !== null && followed.bookId === bookId && followed.chapterId === chapterId && followed.counted !== null ? total - counted + followed.counted : total;
        if (place.writing(bookId).words !== words) place.changeWriting(bookId, { words });
      });
    });
  }

  /** D2, D5, D6, G6: `bookId`'s words today with `change` added, saying when that reaches the daily goal. */
  #add(bookId: string, change: number): Partial<BookWriting> {
    const { place } = this.#options;
    const day = this.#day();
    const writing = place.writing(bookId);
    const was = todayOf(writing, day);
    const now = addToday(writing, day, change);
    const key = `${bookId} ${day}`;
    if (reached(was, now.today, writing.dailyGoal) && !this.#said.has(key) && writing.dailyGoal !== null) {
      this.#said.add(key);
      // Cleared first, so news worded as the last (another book's, another day's) is still news.
      this.#news.set(null);
      this.#news.set({ goal: writing.dailyGoal });
    }
    return now;
  }

  /**
   * B1–B5: counts the open book for the dialog: its live chapters (the open one as typed) and the selection in the open
   * chapter. An answer comes in only while it is the last count asked for and the dialog still wants it.
   */
  countBook(): void {
    const id = ++this.#measures;
    this.#measure.set({ kind: 'counting' });
    const { place } = this.#options;
    const chapterId = place.chapterId();
    const range = place.editor()?.selection ?? null;
    void place.texts().then(async (texts) => {
      if (id !== this.#measures) return;
      if (texts === null) {
        this.#measure.set({ kind: 'failed' });
        return;
      }
      const open = texts.chapters.findIndex((chapter) => chapter.id === chapterId);
      const selection = chapterId !== null && open >= 0 && range !== null && range.from < range.to ? { chapter: chapterId, from: range.from, to: range.to } : null;
      const outcome = await this.#options.run({ kind: 'count', chapters: texts.chapters, selection });
      if (id !== this.#measures) return;
      if (outcome.kind !== 'counted') {
        this.#measure.set({ kind: 'failed' });
        return;
      }
      let chapter: number | null = null;
      let book = 0;
      for (const [index, words] of outcome.counts.entries()) {
        book += words;
        if (index === open) chapter = words;
      }
      this.#measure.set({ kind: 'counted', chapter, selection: outcome.selection, book, unreadable: texts.unreadable });
    });
  }

  /** B7: the open book or chapter changed, or the book was read again: counted again while the dialog shows counts. */
  bookChanged(): void {
    if (this.#measure.get().kind !== 'idle') this.countBook();
  }

  /** B5: the dialog closed: an answer still to come is dropped. */
  close(): void {
    this.#measures += 1;
    this.#measure.set({ kind: 'idle' });
  }

  /** G2, G3: sets the open book's book or daily goal from what the writer typed; false, keeping it, when refused. */
  setGoal(which: 'bookGoal' | 'dailyGoal', text: string): boolean {
    const bookId = this.#options.place.bookId();
    const value = readGoal(text);
    if (bookId === null || value === 'refused') return false;
    this.#options.place.changeWriting(bookId, which === 'bookGoal' ? { bookGoal: value } : { dailyGoal: value });
    return true;
  }
}
